import { analyticsService } from '../index-business';

export interface UserSubscriptionEventData {
  ownerId?: string;
  subscriptionData?: any;
  tariffPlanId?: string;
  invoiceId?: string;
  subscriptionId?: string;
  [key: string]: any;
}

export class UserSubscriptionTracker {

  static trackGetLinkForSubscriptionPayment(data: {
    ownerId: string;
    subscriptionData: any;
    success: boolean;
    error?: string;
    duration?: number;
    result?: any;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      tariff_plan_id: data.subscriptionData?.tariffPlanId,
      payment_provider: data.subscriptionData?.paymentProvider || 'unknown',
      subscription_type: data.subscriptionData?.subscriptionType || 'monthly',
      subscription_amount: data.subscriptionData?.amount,
      subscription_currency: data.subscriptionData?.currency || 'UAH',
      subscription_period: data.subscriptionData?.period,
      has_promo_code: data.subscriptionData?.promoCode ? true : false,
      promo_code: data.subscriptionData?.promoCode,
      payment_link_generated: data.success,
      payment_link_url: data.result?.paymentUrl ? 'has_url' : 'no_url',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'getLinkForSubscriptionPayment',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Subscription Payment Link Generated', eventData);
  }

  static trackGetTariffPlans(data: {
    success: boolean;
    error?: string;
    duration?: number;
    result?: any;
  }) {
    const eventData = {
      tariff_plans_count: data.result?.plans?.length || 0,
      available_currencies: data.result?.currencies || [],
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'getTariffPlans',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Tariff Plans Retrieved', eventData);
  }

  static trackGetDetailedSubscriptionData(data: {
    ownerId: string;
    success: boolean;
    error?: string;
    duration?: number;
    result?: any;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      has_active_subscription: data.result?.subscription?.isActive || false,
      subscription_status: data.result?.subscription?.status,
      subscription_plan: data.result?.subscription?.planName,
      subscription_amount: data.result?.subscription?.amount,
      subscription_currency: data.result?.subscription?.currency,
      days_until_renewal: data.result?.subscription?.daysUntilRenewal,
      unpaid_invoices_count: data.result?.unpaidInvoices?.length || 0,
      total_unpaid_amount: data.result?.totalUnpaidAmount,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'getDetailedSubscriptionData',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Detailed Subscription Data Retrieved', eventData);
  }

  static trackGetSubscriptionData(data: {
    ownerId: string;
    success: boolean;
    error?: string;
    duration?: number;
    result?: any;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      has_subscription: data.result?.subscription ? true : false,
      subscription_status: data.result?.subscription?.status,
      subscription_plan: data.result?.subscription?.planName,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'getSubscriptionData',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Subscription Data Retrieved', eventData);
  }

  static trackEditSubscription(data: {
    ownerId: string;
    subscriptionData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const changedFields = data.subscriptionData ? Object.keys(data.subscriptionData) : [];

    const eventData = {
      owner_id: data.ownerId,
      changed_fields: changedFields,
      new_tariff_plan_id: data.subscriptionData?.tariffPlanId,
      new_subscription_type: data.subscriptionData?.subscriptionType,
      new_amount: data.subscriptionData?.amount,
      new_currency: data.subscriptionData?.currency,
      subscription_upgrade: data.subscriptionData?.isUpgrade || false,
      subscription_downgrade: data.subscriptionData?.isDowngrade || false,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'editSubscription',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Subscription Edited', eventData);
  }

  static trackCancelSubscription(data: {
    ownerId: string;
    success: boolean;
    error?: string;
    duration?: number;
    result?: any;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      cancellation_reason: data.result?.cancellationReason || 'not_specified',
      cancellation_type: data.result?.cancellationType || 'immediate', // immediate, end_of_period
      refund_amount: data.result?.refundAmount,
      cancellation_effective_date: data.result?.effectiveDate,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'cancelSubscription',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Subscription Cancelled', eventData);
  }

  static trackResumeSubscription(data: {
    ownerId: string;
    success: boolean;
    error?: string;
    duration?: number;
    result?: any;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      resume_type: data.result?.resumeType || 'immediate', // immediate, next_period
      resume_effective_date: data.result?.effectiveDate,
      new_amount: data.result?.newAmount,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'resumeSubscription',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Subscription Resumed', eventData);
  }

  static trackPayInvoice(data: {
    ownerId: string;
    invoiceId: string;
    success: boolean;
    error?: string;
    duration?: number;
    result?: any;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      invoice_id: data.invoiceId,
      invoice_amount: data.result?.amount,
      invoice_currency: data.result?.currency,
      payment_method: data.result?.paymentMethod,
      payment_status: data.result?.paymentStatus,
      transaction_id: data.result?.transactionId,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'payInvoice',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Invoice Paid', eventData);
  }

  static trackGetSubscriptionInfo(data: {
    ownerId: string;
    success: boolean;
    error?: string;
    duration?: number;
    result?: any;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      subscription_status: data.result?.status,
      subscription_plan: data.result?.planName,
      billing_cycle: data.result?.billingCycle,
      next_billing_date: data.result?.nextBillingDate,
      usage_stats: data.result?.usageStats ? 'has_usage_data' : 'no_usage_data',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'getSubscriptionInfo',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Subscription Info Retrieved', eventData);
  }

  static trackUserSubscriptionApiCall(endpoint: string, data: UserSubscriptionEventData & {
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      ...data,
      event_type: 'api_call',
      api_endpoint: endpoint,
      timestamp: new Date().toISOString()
    };

    analyticsService.track(`User Subscription API: ${endpoint}`, eventData);
  }
}

export const trackUserSubscriptionSuccess = (endpoint: string, data: UserSubscriptionEventData, duration?: number) => {
  UserSubscriptionTracker.trackUserSubscriptionApiCall(endpoint, {
    ...data,
    success: true,
    duration
  });
};

export const trackUserSubscriptionError = (endpoint: string, error: string, data: UserSubscriptionEventData, duration?: number) => {
  UserSubscriptionTracker.trackUserSubscriptionApiCall(endpoint, {
    ...data,
    success: false,
    error,
    duration
  });
};

export default UserSubscriptionTracker;
